// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    srcType = "srcType",
    url = "url",
    jsScript = "jsScript",
    scriptTitle = "scriptTitle",
}

interface ScriptWidgetProps extends WidgetProps {
    fields: {
        [fields.srcType]: string;
        [fields.url]: string;
        [fields.jsScript]: string;
        [fields.scriptTitle]: string;
    };
}

type Props = ScriptWidgetProps;

const ScriptWidget: FC<Props> = ({ fields }) => {
    const shellContext = useShellContext();
    const refContainer = React.useRef(null);

    if (fields.srcType === "jsURL" && fields.url.length !== 0) {
        React.useEffect(() => {
            const script = document.createElement("script");
            script.type = "text/javascript";
            script.src = fields.url;
            script.async = true;
            try {
                (refContainer.current as any)?.appendChild(script);
            } catch (e) {}
            return () => {
                document.body.removeChild(script);
                (refContainer.current as any)?.removeChild(script);
            };
        }, []);
    } else if (fields.srcType === "jsText" && fields.jsScript.length !== 0) {
        React.useEffect(() => {
            const script = document.createElement("script");
            script.type = "text/javascript";
            script.async = true;
            const code = fields.jsScript;
            try {
                script.appendChild(document.createTextNode(code));
                (refContainer.current as any)?.appendChild(script);
            } catch (e) {
                script.text = code;
                (refContainer.current as any)?.appendChild(script);
            }
            return () => {
                (refContainer.current as any)?.removeChild(script);
            };
        }, []);
    }

    if (shellContext.isEditing) {
        return (
            <StyledWrapper
                css={css`
                    background: #e5e5e5;
                    padding: 10px;
                `}
            >
                {`${translate("Script Widget")} - ${fields.scriptTitle || fields.url}`}
            </StyledWrapper>
        );
    }

    return (
        <>
            <div ref={refContainer} />
        </>
    );
};

export const definition: WidgetDefinition = {
    group: "Basic",
    displayName: "Script Widget",
    fieldDefinitions: [
        {
            name: fields.srcType,
            displayName: "Source Type",
            editorTemplate: "RadioButtonsField",
            options: [
                {
                    displayName: "URL",
                    value: "jsURL",
                },
                {
                    displayName: "Java Script Text",
                    value: "jsText",
                },
            ],
            defaultValue: "jsURL",
            fieldType: "General",
            sortOrder: 1,
        },
        {
            name: fields.url,
            displayName: "URL",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            placeholder: "URL",
            sortOrder: 2,
            isVisible: item => item.fields.srcType === "jsURL",
        },
        {
            name: fields.jsScript,
            displayName: "Java Script Text",
            editorTemplate: "MultilineTextField",
            defaultValue: "",
            fieldType: "General",
            placeholder: "Java Script Text",
            sortOrder: 3,
            isVisible: item => item.fields.srcType === "jsText",
        },
        {
            name: fields.scriptTitle,
            displayName: "Script title, for CMS tracking",
            editorTemplate: "TextField",
            fieldType: "General",
            defaultValue: "",
            sortOrder: 4,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: ScriptWidget,
    definition,
};

export default widgetModule;
